import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';

import { CharacteristicConsultListComponent } from './characteristic-consult-list/characteristic-consult-list.component';
import { CharacteristicConsultListService } from './shared/characteristic-consult-list.service';
import { countActiveFilters } from './shared/characteristic-consult.utils';

@Component({
  standalone: false,
  selector: 'app-characteristic-consult',
  templateUrl: './characteristic-consult.component.html',
  styleUrls: ['./characteristic-consult.component.scss'],
})
export class CharacteristicConsultComponent implements OnInit, OnDestroy {
  @ViewChild(CharacteristicConsultListComponent)
  private listComponent?: CharacteristicConsultListComponent;

  private readonly destroy$ = new Subject<void>();

  showFilter = false;
  showDetailsDrawer = false;
  selectedCreditCodeForDetails: string | null = null;
  filtersTotal = 0;
  valueTotal = 0;
  showExportSuccessBanner = false;
  showLoadErrorBanner = false;
  showFilterUpdateNotice = false;
  isErrorModalOpen = false;
  errorModalMessage = 'Não foi possível concluir a operação. Tente novamente.';

  constructor(
    private readonly characteristicConsultListService: CharacteristicConsultListService,
  ) {}

  get filterButtonAriaLabel(): string {
    if (this.filtersTotal <= 0) {
      return 'Filtrar';
    }

    return this.filtersTotal === 1
      ? 'Filtrar, 1 filtro aplicado'
      : `Filtrar, ${this.filtersTotal} filtros aplicados`;
  }

  ngOnInit(): void {
    this.characteristicConsultListService.filters
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        this.filtersTotal = countActiveFilters(filters);
        this.showFilterUpdateNotice = this.filtersTotal > 0;
      });

    this.characteristicConsultListService.patchFilters({
      carbonCreditCode: undefined,
      initialDate: undefined,
      finalDate: undefined,
      registerCode: undefined,
      holderCode: undefined,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onValueFilterTotal(value: number): void {
    this.filtersTotal = value;
  }

  onValueTotal(value: number): void {
    this.valueTotal = value;
    this.showLoadErrorBanner = false;
  }

  openAdvancedFilter(): void {
    this.showFilter = true;
  }

  onFilterDrawerClosed(): void {
    this.showFilter = false;
  }

  onFilterNoticeDismiss(): void {
    this.showFilterUpdateNotice = false;
  }

  onFilterNoticeUndo(): void {
    this.characteristicConsultListService.setFilters({});
    this.characteristicConsultListService.isSetDate.next(false);
    this.showFilterUpdateNotice = false;
  }

  openDetail(carbon: CarbonCredit): void {
    const code = carbon.carbonCreditCode?.trim();
    if (!code) {
      return;
    }

    this.selectedCreditCodeForDetails = code;
    this.showDetailsDrawer = true;
  }

  onDetailsDrawerClosed(): void {
    this.showDetailsDrawer = false;
    this.selectedCreditCodeForDetails = null;
  }

  onDetailsLoadFailed(): void {
    this.showDetailsDrawer = false;
    this.selectedCreditCodeForDetails = null;
    this.openErrorModal('Não foi possível carregar os detalhes do crédito.');
  }

  onListLoadFailed(): void {
    this.showLoadErrorBanner = true;
    this.showExportSuccessBanner = false;
  }

  onExportSucceeded(): void {
    this.showExportSuccessBanner = true;
    this.showLoadErrorBanner = false;
  }

  onExportFailed(): void {
    this.openErrorModal('Não foi possível exportar a consulta. Tente novamente.');
  }

  onLoadErrorDismiss(): void {
    this.showLoadErrorBanner = false;
  }

  onLoadErrorRetry(): void {
    this.showLoadErrorBanner = false;
    this.listComponent?.reloadCurrentQuery();
  }

  onExportSuccessDismiss(): void {
    this.showExportSuccessBanner = false;
  }

  onErrorModalClosed(): void {
    this.isErrorModalOpen = false;
  }

  exportCsv(): void {
    this.listComponent?.imprimir();
  }

  private openErrorModal(message: string): void {
    this.errorModalMessage = message;
    this.isErrorModalOpen = true;
  }
}
